import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { SUPPORT_MASTHEAD_HEIGHT } from '../supportLayoutConstants';

export const supportSharedStyles = StyleSheet.create({
	supportNav: {
		boxShadow: 'none',
		boxSizing: 'border-box',
		borderRight: `1px solid ${THEME.colors.border.light}`,
	},
	supportNavHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: SUPPORT_MASTHEAD_HEIGHT,
		minHeight: SUPPORT_MASTHEAD_HEIGHT,
		flex: 'none',
		textDecoration: 'none',
		':focus-visible': {
			outline: `2px solid ${THEME.colors.background.light}`,
			outlineOffset: -4,
		},
	},

	// Shared app bar (rendered into the masthead portal) for all support routes.
	supportAppBar: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		minWidth: 0,
		width: '100%',
		'@media (max-width: 767px)': { width: 'auto', flex: 1 },
	},
	supportTabRow: {
		display: 'flex',
		alignItems: 'stretch',
		gap: 2,
		height: SUPPORT_MASTHEAD_HEIGHT,
	},
	supportTab: {
		display: 'flex',
		alignItems: 'center',
		gap: 7,
		height: '100%',
		padding: '0 13px',
		background: 'transparent',
		border: 'none',
		borderBottom: '2px solid transparent',
		borderRadius: 0,
		cursor: 'pointer',
		fontSize: 14,
		fontWeight: 400,
		color: THEME.colors.text.light,
		':hover': { color: THEME.colors.text.main, background: 'transparent' },
	},
	supportTabActive: {
		color: THEME.colors.primary.main,
		borderBottomColor: THEME.colors.primary.main,
		fontWeight: 600,
	},
	supportTabDivider: {
		width: 1,
		height: 26,
		backgroundColor: THEME.colors.border.light,
		margin: '0 4px',
	},
	supportMailboxSwitcher: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		background: 'transparent',
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		padding: '6px 10px',
		fontSize: 14,
		fontWeight: 500,
		color: THEME.colors.text.main,
	},
	supportMailboxDot: {
		width: 8,
		height: 8,
		borderRadius: '50%',
		backgroundColor: THEME.colors.success.dark,
		flex: 'none',
	},
	newTicketDrawerIcon: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		width: 32,
		height: 24,
	},
	newTicketDrawerPlus: {
		position: 'absolute',
		right: 0,
		bottom: 1,
		pointerEvents: 'none',
	},
	supportDesktopControls: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		minWidth: 0,
		width: '100%',
		'@media (max-width: 767px)': { display: 'none' },
	},
	supportPhoneHeader: {
		display: 'none',
		'@media (max-width: 767px)': {
			display: 'flex',
			alignItems: 'center',
			gap: THEME.spacing.xs,
			minWidth: 0,
			flex: 1,
		},
	},
	supportPhoneTitle: {
		minWidth: 0,
		flex: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		fontWeight: THEME.typography.fontWeights.bold,
	},
	supportPhoneAction: {
		minWidth: 44,
		height: 44,
		padding: `${THEME.spacing.xs}px`,
	},
	// Back to the ticket list. Labelled rather than an icon alone: it is the only control in the
	// phone header while a ticket is open, so it can afford the width.
	supportPhoneBack: {
		flex: 'none',
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		height: 44,
		padding: `0 ${THEME.spacing.sm}px 0 ${THEME.spacing.xs}px`,
		color: THEME.colors.primary.main,
		fontSize: THEME.typography.fontSizes.md - 1,
	},
	supportPhoneMailboxTrigger: {
		minWidth: 0,
		maxWidth: 170,
		height: 44,
		flex: 1,
	},
	supportPhoneMenu: {
		minWidth: 180,
	},

	notificationBellWrap: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		marginLeft: 'auto',
	},
	notificationBellBtn: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 36,
		height: 36,
		padding: 0,
		color: THEME.colors.text.light,
		':hover': { color: THEME.colors.text.main },
	},
	notificationBellBadge: {
		position: 'absolute',
		top: 2,
		right: 0,
		minWidth: 16,
		height: 16,
		padding: '0 4px',
		borderRadius: 8,
		backgroundColor: THEME.colors.danger.main,
		color: THEME.colors.danger.contrastText,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		lineHeight: '16px',
		textAlign: 'center',
	},
	notificationPopover: {
		width: 380,
		maxHeight: 480,
		display: 'flex',
		flexDirection: 'column',
		padding: 0,
		overflow: 'hidden',
	},
	notificationHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	notificationList: {
		overflowY: 'auto',
		flex: 1,
	},
	notificationRow: {
		display: 'flex',
		gap: THEME.spacing.sm,
		alignItems: 'flex-start',
		width: '100%',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		textAlign: 'left',
		borderRadius: 0,
		':hover': { backgroundColor: THEME.colors.hover.light },
	},
	notificationRowUnread: {
		backgroundColor: THEME.colors.background.main,
	},
	notificationUnreadDot: {
		width: 8,
		height: 8,
		marginTop: 6,
		borderRadius: '50%',
		backgroundColor: THEME.colors.primary.main,
		flex: 'none',
	},
	notificationReadSpacer: {
		width: 8,
		flex: 'none',
	},
	notificationBody: {
		display: 'block',
		minWidth: 0,
		flex: 1,
	},
	notificationTitle: {
		display: 'block',
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
		whiteSpace: 'normal',
	},
	notificationMessage: {
		display: 'block',
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		...THEME.common.truncate,
	},
	notificationTime: {
		display: 'block',
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
	},
	notificationEmpty: {
		padding: THEME.spacing.lg,
		textAlign: 'center',
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},

	// Master→detail shell. The detail view is a white sidebar + scrollable content pane; the
	// landing (mailbox list) drops the sidebar for a single centered column, matching the prototype.
	layout: {
		display: 'flex',
		height: '100%',
		minHeight: 0,
		overflow: 'hidden' as const,
		'@media (max-width: 767px)': { flexDirection: 'column', overflowY: 'auto' as const },
		// The support surface is all Sora (per Sean, diverging from the prototype's Lexend body
		// copy); components-v2 controls keep their own font defaults.
		fontFamily: THEME.typography.fontFamilies.sora,
		backgroundColor: THEME.colors.background.main,
	},
	// Landing: one centered, scrolling column (no sidebar).
	landingScroll: {
		height: '100%',
		minHeight: 0,
		overflowY: 'auto' as const,
		fontFamily: THEME.typography.fontFamilies.sora,
		backgroundColor: THEME.colors.background.main,
	},
	landing: {
		display: 'flex',
		flexDirection: 'column' as const,
		width: '100%',
		boxSizing: 'border-box',
		maxWidth: 1100,
		margin: '0 auto',
		padding: '28px 28px 52px',
		'@media (max-width: 767px)': { padding: `${THEME.spacing.md}px ${THEME.spacing.sm}px ${THEME.spacing.xl}px` },
	},
	leftSidebar: {
		width: 236,
		minWidth: 236,
		flex: 'none',
		borderRight: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.light,
		overflowY: 'auto' as const,
		padding: '20px 14px',
		display: 'flex',
		flexDirection: 'column' as const,
		'@media (max-width: 767px)': {
			width: '100%',
			minWidth: 0,
			borderRight: 'none',
			borderBottom: `1px solid ${THEME.colors.border.light}`,
			padding: THEME.spacing.sm,
		},
	},
	navSection: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 1,
	},
	navSectionTitle: {
		padding: '6px 10px 8px',
		fontSize: 11,
		fontWeight: 600,
		textTransform: 'uppercase' as const,
		color: THEME.colors.text.light,
		letterSpacing: 0.5,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap' as const,
	},
	content: {
		flex: 1,
		minWidth: 0,
		overflowY: 'auto' as const,
		padding: '30px 36px 52px',
		'@media (max-width: 767px)': { padding: `${THEME.spacing.md}px ${THEME.spacing.sm}px ${THEME.spacing.xl}px` },
	},

	// Shared modal-field primitives, reused by every support Dialog (GitHub issue modals,
	// change-customer modal). Housed here rather than a feature-specific styleset so no modal
	// depends on another feature's styles.
	modalBody: {
		flex: 1,
		overflowY: 'auto' as const,
		padding: 20,
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 14,
	},
	modalLabel: {
		display: 'block',
		fontSize: 13,
		fontWeight: 600,
		color: THEME.colors.text.main,
		marginBottom: 4,
	},
	modalInput: {
		width: '100%',
		padding: '8px 10px',
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		fontSize: 13,
		fontFamily: 'inherit',
		boxSizing: 'border-box' as const,
		':focus': {
			outline: 'none',
			borderColor: THEME.colors.primary.main,
		},
	},
	modalInputError: {
		borderColor: THEME.colors.danger.main,
	},
	modalErrorText: {
		fontSize: 12,
		color: THEME.colors.danger.main,
		marginTop: 2,
	},
	modalSpinnerRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '16px 0',
		gap: 8,
		fontSize: 13,
		color: THEME.colors.text.light,
	},
});
